import React, { useCallback, useEffect, useState } from 'react';
import { Card, Typography, Tag, Table, Spin, Alert, Space, Empty } from 'antd';
import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';

const { Title, Text, Paragraph } = Typography;

interface MyPermissionsResponse {
  role: { id: string | null; type: string | null; name: string | null };
  structured: Record<string, string[]>;
  flat: string[];
  isSuperAdmin: boolean;
}

/**
 * Self-permissions page — read-only display of what the logged-in admin
 * can do. Linked from the header dropdown. Calls
 * GET /admin/auth/me/permissions via adminApi (which uses the configured
 * axiosInstance with the correct baseURL + bearer token interceptor).
 */
export const MyPermissionsPage: React.FC = () => {
  const [data, setData] = useState<MyPermissionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPermissions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getMyPermissions();
      setData(res.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchPermissions();
  }, [fetchPermissions]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Alert message="Error" description={error} type="error" showIcon />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No permission data" />
      </div>
    );
  }

  const resourceRows = Object.entries(data.structured)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([resource, actions]) => ({
      resource,
      actions,
    }));

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 8 }}>
        <SafetyCertificateOutlined /> My Permissions
      </Title>
      <Paragraph type="secondary">
        Read-only view of what your admin role can do. If you click an action that isn't listed
        here, the API returns 403 and the toast will tell you exactly which permission you're
        missing.
      </Paragraph>

      <Card style={{ marginBottom: 16 }} size="small">
        <Space size="middle">
          <Text strong>Role:</Text>
          {data.role.name ? <Tag color="blue">{data.role.name}</Tag> : <Tag>None</Tag>}
          {data.role.type && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({data.role.type})
            </Text>
          )}
          {data.isSuperAdmin && (
            <Tag color="gold">
              <LockOutlined /> Super Admin — bypasses all gates
            </Tag>
          )}
        </Space>
      </Card>

      {data.isSuperAdmin ? (
        <Card>
          <Empty
            image={<LockOutlined style={{ fontSize: 48, color: '#faad14' }} />}
            description={
              <>
                <Title level={5}>Super Admin</Title>
                <Text type="secondary">
                  All resource:action gates bypassed. You can do everything the admin panel exposes.
                </Text>
              </>
            }
          />
        </Card>
      ) : resourceRows.length === 0 ? (
        <Card>
          <Empty description="No permissions granted" />
        </Card>
      ) : (
        <Card title={`Granted permissions (${data.flat.length} total)`}>
          <Table
            dataSource={resourceRows}
            rowKey="resource"
            pagination={false}
            size="small"
            columns={[
              {
                title: 'Resource',
                dataIndex: 'resource',
                key: 'resource',
                width: 200,
                render: (resource: string) => <Text strong>{resource}</Text>,
              },
              {
                title: 'Allowed Actions',
                dataIndex: 'actions',
                key: 'actions',
                render: (actions: string[]) => (
                  <Space size={[4, 4]} wrap>
                    {actions.map((a) => (
                      <Tag key={a} color="green">
                        {a}
                      </Tag>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
};

export default MyPermissionsPage;
